import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_LANGUAGE, isLanguage, type Language } from '@/i18n';
import { DEFAULT_THEME, isThemeName, type ThemeName } from '@/ui/theme/themes';

/** Application-level preferences. Small, user-chosen and persisted across sessions. */
export interface AppState {
  readonly language: Language;
  readonly theme: ThemeName;
  setLanguage(language: Language): void;
  setTheme(theme: ThemeName): void;
}

export const SETTINGS_STORAGE_KEY = 'plcsim.settings';

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      language: DEFAULT_LANGUAGE,
      theme: DEFAULT_THEME,
      setLanguage: (language) => set({ language }),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: SETTINGS_STORAGE_KEY,
      version: 1,
      partialize: ({ language, theme }) => ({ language, theme }),
      // Stored settings may be stale or hand-edited; ignore anything that is not valid.
      merge: (persisted, current) => {
        const stored = (persisted ?? {}) as Partial<Record<'language' | 'theme', unknown>>;
        return {
          ...current,
          language: isLanguage(stored.language) ? stored.language : current.language,
          theme: isThemeName(stored.theme) ? stored.theme : current.theme,
        };
      },
    },
  ),
);
